import React, { useState, useCallback, useEffect } from 'react';
import {
    Container, Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Button, IconButton, FormControl, Select, MenuItem,
    Chip, Divider, Tooltip, InputAdornment, Stack, Collapse, CircularProgress, Grid,
    Card, CardContent, Alert
} from '@mui/material';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Delete as DeleteIcon,
    ContentCopy as ContentCopyIcon,
    Payments as PaymentsIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    PushPin as PushPinIcon,
    PushPinOutlined as PushPinOutlinedIcon,
    PersonAdd as PersonAddIcon,
    Person as PersonIcon,
    Work as WorkIcon,
    ExpandMore as ExpandMoreIcon,
    Groups as GroupsIcon,
    CalendarMonth as CalendarMonthIcon,
    AccountBalance as AccountBalanceIcon,
    TrendingUp as TrendingUpIcon,
    Clear as ClearIcon,
    TableChart as TableChartIcon
} from '@mui/icons-material';

interface ConsultantRow {
    id: string;
    name: string;
    role: string;
    rate: number;
    days: number;
    sourceRate?: number; // Original rate from search (for reference)
    sourceCurrency?: string; // Original currency from search
}

// Staff search result from iod_parc_allocations
interface StaffAllocation {
    project_number: string;
    client_name: string;
    project_name: string;
    role: string;
    fee_per_day: number | null;
    rca_rate: number | null;
    currency: string;
}

interface StaffSearchResult {
    person_name: string;
    person_type: string;
    compass_name: string | null;
    compass_associate_id: number | null;
    project_count: number;
    allocations: StaffAllocation[];
}

const CostEstimatorPage: React.FC = () => {
    // Consultant rows state - now with free text name and role
    const [consultants, setConsultants] = useState<ConsultantRow[]>([
        { id: '1', name: '', role: '', rate: 0, days: 0 }
    ]);
    
    // Staff search state
    const [staffSearchQuery, setStaffSearchQuery] = useState('');
    const [staffSearchResults, setStaffSearchResults] = useState<StaffSearchResult[]>([]);
    const [isSearchingStaff, setIsSearchingStaff] = useState(false);
    const [pinnedStaff, setPinnedStaff] = useState<StaffSearchResult[]>([]);
    const [expandedStaff, setExpandedStaff] = useState<string | null>(null);

    // Additional cost line items
    const [costLineItems, setCostLineItems] = useState<{name: string; amount: number}[]>([
        { name: 'Travel & Accommodation', amount: 0 },
        { name: 'Sub-contractors', amount: 0 },
        { name: 'Overhead (20%)', amount: 0 },
        { name: 'Contingency (5%)', amount: 0 }
    ]);

    // Currency options
    const CURRENCY_OPTIONS = [
        { code: 'GBP', symbol: '£', label: 'GBP (£)' },
        { code: 'USD', symbol: '$', label: 'USD ($)' },
        { code: 'EUR', symbol: '€', label: 'EUR (€)' },
        { code: 'CHF', symbol: 'CHF ', label: 'CHF' },
        { code: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
        { code: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
        { code: 'JPY', symbol: '¥', label: 'JPY (¥)' },
        { code: 'CNY', symbol: '¥', label: 'CNY (¥)' },
        { code: 'INR', symbol: '₹', label: 'INR (₹)' },
        { code: 'SEK', symbol: 'kr', label: 'SEK (kr)' },
        { code: 'NOK', symbol: 'kr', label: 'NOK (kr)' },
        { code: 'DKK', symbol: 'kr', label: 'DKK (kr)' },
        { code: 'ZAR', symbol: 'R', label: 'ZAR (R)' },
        { code: 'AED', symbol: 'د.إ', label: 'AED' },
        { code: 'SAR', symbol: '﷼', label: 'SAR' },
        { code: 'SGD', symbol: 'S$', label: 'SGD (S$)' },
        { code: 'HKD', symbol: 'HK$', label: 'HKD (HK$)' },
        { code: 'NZD', symbol: 'NZ$', label: 'NZD (NZ$)' },
        { code: 'MXN', symbol: 'Mex$', label: 'MXN' },
        { code: 'BRL', symbol: 'R$', label: 'BRL (R$)' }
    ];

    const [selectedCurrency, setSelectedCurrency] = useState<string>('GBP');
    const getCurrencySymbol = () => CURRENCY_OPTIONS.find(c => c.code === selectedCurrency)?.symbol || '£';

    // Exchange rates relative to GBP
    const [exchangeRates, setExchangeRates] = useState<{[key: string]: number}>({
        GBP: 1.00,
        USD: 1.27,
        EUR: 1.17,
        CHF: 1.12,
        CAD: 1.72,
        AUD: 1.93,
        JPY: 191.50,
        CNY: 9.15,
        INR: 106.00,
        SEK: 13.20,
        NOK: 13.50,
        DKK: 8.70,
        ZAR: 23.00,
        AED: 4.66,
        SAR: 4.76,
        SGD: 1.70,
        HKD: 9.90,
        NZD: 2.10,
        MXN: 21.80,
        BRL: 7.30
    });
    const [isLoadingRates, setIsLoadingRates] = useState(false);
    const [ratesLastUpdated, setRatesLastUpdated] = useState<Date | null>(null);
    const [copied, setCopied] = useState(false);

    // Fetch live exchange rates
    const fetchExchangeRates = useCallback(async () => {
        setIsLoadingRates(true);
        try {
            const response = await fetch('https://api.frankfurter.app/latest?from=GBP');
            if (response.ok) {
                const data = await response.json();
                setExchangeRates(prev => ({
                    ...prev,
                    GBP: 1.00,
                    ...data.rates
                }));
                setRatesLastUpdated(new Date());
            }
        } catch (err) {
            console.warn('Failed to fetch exchange rates, using defaults:', err);
        } finally {
            setIsLoadingRates(false);
        }
    }, []);

    // Fetch rates on mount
    useEffect(() => {
        fetchExchangeRates();
    }, [fetchExchangeRates]);

    // Handle currency change with rate conversion
    const handleCurrencyChange = useCallback((newCurrency: string) => {
        const oldCurrency = selectedCurrency;
        if (oldCurrency === newCurrency) return;

        const oldRate = exchangeRates[oldCurrency] || 1;
        const newRate = exchangeRates[newCurrency] || 1;
        const conversionFactor = newRate / oldRate;

        // Convert consultant rates
        setConsultants(prev => prev.map(c => ({
            ...c,
            rate: Math.round(c.rate * conversionFactor)
        })));

        // Convert additional cost amounts
        setCostLineItems(prev => prev.map(item => ({
            ...item,
            amount: Math.round(item.amount * conversionFactor)
        })));

        setSelectedCurrency(newCurrency);
    }, [selectedCurrency, exchangeRates]);

    // Calculations
    const calculateTotalDays = () => consultants.reduce((sum, c) => sum + c.days, 0);
    const calculateConsultantCost = () => consultants.reduce((sum, c) => sum + (c.days * c.rate), 0);
    const calculateLineItemsTotal = () => costLineItems.reduce((sum, item) => sum + item.amount, 0);
    const calculateGrandTotal = () => calculateConsultantCost() + calculateLineItemsTotal();

    // Investment band based on GBP equivalent
    const getInvestmentBand = (total: number) => {
        const currentRate = exchangeRates[selectedCurrency] || 1;
        const gbpTotal = total / currentRate;
        if (gbpTotal < 100000) return 'Small';
        if (gbpTotal <= 600000) return 'Medium';
        return 'Large';
    };

    // Consultant management
    const handleUpdateConsultant = (id: string, field: keyof ConsultantRow, value: string | number) => {
        setConsultants(prev => prev.map(c => {
            if (c.id !== id) return c;
            if (field === 'name') return { ...c, name: value as string };
            if (field === 'role') return { ...c, role: value as string };
            if (field === 'rate') return { ...c, rate: Math.max(0, Number(value)) };
            if (field === 'days') return { ...c, days: Math.max(0, Number(value)) };
            return c;
        }));
    };

    const handleAddConsultant = () => {
        const newId = String(Date.now());
        setConsultants(prev => [...prev, { id: newId, name: '', role: '', rate: 0, days: 0 }]);
    };

    const handleRemoveConsultant = (id: string) => {
        setConsultants(prev => prev.filter(c => c.id !== id));
    };
    
    // Staff search functions
    const searchStaff = useCallback(async (query: string) => {
        if (query.length < 2) {
            setStaffSearchResults([]);
            return;
        }
        
        setIsSearchingStaff(true);
        try {
            const response = await fetch(`/api/staff-search?q=${encodeURIComponent(query)}&limit=15`);
            if (response.ok) {
                const data = await response.json();
                setStaffSearchResults(data.results || []);
            }
        } catch (err) {
            console.error('Staff search failed:', err);
        } finally {
            setIsSearchingStaff(false);
        }
    }, []);
    
    // Debounced staff search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (staffSearchQuery) {
                searchStaff(staffSearchQuery);
            } else {
                setStaffSearchResults([]);
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [staffSearchQuery, searchStaff]);
    
    const handlePinStaff = (staff: StaffSearchResult) => {
        if (!pinnedStaff.find(p => p.person_name === staff.person_name)) {
            setPinnedStaff(prev => [...prev, staff]);
        }
    };
    
    const handleUnpinStaff = (personName: string) => {
        setPinnedStaff(prev => prev.filter(p => p.person_name !== personName));
    };
    
    const handleAddStaffToTeam = (staff: StaffSearchResult, allocation?: StaffAllocation) => {
        const newId = String(Date.now());
        const rate = allocation?.fee_per_day || allocation?.rca_rate || 0;
        const role = allocation?.role || staff.allocations[0]?.role || '';
        const currency = allocation?.currency || 'GBP';
        setConsultants(prev => [...prev, { 
            id: newId, 
            name: staff.person_name, 
            role: role,
            rate: rate,
            days: 0,
            sourceRate: rate,
            sourceCurrency: currency
        }]);
    };
    
    // Clear all team members
    const handleClearTeam = () => {
        setConsultants([{ id: '1', name: '', role: '', rate: 0, days: 0 }]);
    };
    
    // Copy as markdown table
    const handleCopyAsTable = useCallback(() => {
        const symbol = getCurrencySymbol();
        const headers = '| Name | Role | Day Rate | Days | Cost |';
        const separator = '|------|------|----------|------|------|';
        const rows = consultants
            .filter(c => c.name || c.days > 0)
            .map(c => `| ${c.name || 'TBD'} | ${c.role || '-'} | ${symbol}${c.rate} | ${c.days} | ${symbol}${(c.days * c.rate).toLocaleString()} |`);
        
        const table = [headers, separator, ...rows].join('\n');
        const summary = `\n\n**Total:** ${symbol}${calculateGrandTotal().toLocaleString()} | **Days:** ${calculateTotalDays()} | **Band:** ${getInvestmentBand(calculateGrandTotal())}`;
        
        navigator.clipboard.writeText(table + summary);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [consultants, selectedCurrency]);

    // Line item management
    const handleAddLineItem = () => {
        setCostLineItems([...costLineItems, { name: 'New Item', amount: 0 }]);
    };

    const handleRemoveLineItem = (index: number) => {
        setCostLineItems(costLineItems.filter((_, i) => i !== index));
    };

    const handleUpdateLineItem = (index: number, field: 'name' | 'amount', value: string | number) => {
        const updated = [...costLineItems];
        if (field === 'name') {
            updated[index].name = value as string;
        } else {
            updated[index].amount = typeof value === 'string' ? parseFloat(value) || 0 : value;
        }
        setCostLineItems(updated);
    };

    // Copy to clipboard
    const handleCopy = useCallback(() => {
        const symbol = getCurrencySymbol();
        const consultantLines = consultants
            .filter(c => c.days > 0)
            .map(c => `  ${c.name || 'TBD'}${c.role ? ` (${c.role})` : ''} @ ${symbol}${c.rate}/day: ${c.days} days = ${symbol}${(c.days * c.rate).toLocaleString()}`);

        const lines = [
            'INDICATIVE COST ESTIMATE',
            '========================',
            `Currency: ${selectedCurrency}`,
            '',
            'Consultant Days:',
            ...consultantLines,
            `  Subtotal: ${calculateTotalDays()} days = ${symbol}${calculateConsultantCost().toLocaleString()}`,
            '',
            'Additional Costs:',
            ...costLineItems.map(item => `  ${item.name}: ${symbol}${item.amount.toLocaleString()}`),
            '',
            `TOTAL ESTIMATE: ${symbol}${calculateGrandTotal().toLocaleString()}`,
            '',
            `Investment Band: ${getInvestmentBand(calculateGrandTotal())}`
        ];
        navigator.clipboard.writeText(lines.join('\n'));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [consultants, costLineItems, selectedCurrency]);

    // Reset calculator
    const handleReset = () => {
        // Reset to GBP first
        setSelectedCurrency('GBP');
        setConsultants([
            { id: '1', name: '', role: '', rate: 0, days: 0 }
        ]);
        setCostLineItems([
            { name: 'Travel & Accommodation', amount: 0 },
            { name: 'Sub-contractors', amount: 0 },
            { name: 'Overhead (20%)', amount: 0 },
            { name: 'Contingency (5%)', amount: 0 }
        ]);
        setPinnedStaff([]);
        setStaffSearchQuery('');
        setStaffSearchResults([]);
    };

    // Count team members with names
    const teamMemberCount = consultants.filter(c => c.name).length;

    return (
        <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, md: 4 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight={600} gutterBottom>
                        <PaymentsIcon sx={{ mr: 1.5, verticalAlign: 'middle', color: 'success.main', fontSize: 36 }} />
                        Cost Estimator
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Build your team and estimate project costs. Search for staff and associates from past projects.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={ratesLastUpdated ? `Updated: ${ratesLastUpdated.toLocaleTimeString()}` : 'Live rates'}>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                                value={selectedCurrency}
                                onChange={(e) => handleCurrencyChange(e.target.value)}
                                disabled={isLoadingRates}
                            >
                                {CURRENCY_OPTIONS.map(opt => (
                                    <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Tooltip>
                    <Tooltip title="Refresh rates">
                        <IconButton onClick={fetchExchangeRates} disabled={isLoadingRates} size="small">
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: 'primary.900', border: '1px solid', borderColor: 'primary.700' }}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <GroupsIcon color="primary" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Team Size</Typography>
                                    <Typography variant="h5" fontWeight={700} color="primary.main">{teamMemberCount}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: 'info.900', border: '1px solid', borderColor: 'info.700' }}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarMonthIcon color="info" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Total Days</Typography>
                                    <Typography variant="h5" fontWeight={700} color="info.main">{calculateTotalDays()}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: 'success.900', border: '1px solid', borderColor: 'success.700' }}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountBalanceIcon color="success" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                                    <Typography variant="h5" fontWeight={700} color="success.main">
                                        {getCurrencySymbol()}{calculateGrandTotal().toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ 
                        bgcolor: getInvestmentBand(calculateGrandTotal()) === 'Small' ? 'success.900' : 
                                 getInvestmentBand(calculateGrandTotal()) === 'Medium' ? 'warning.900' : 'error.900',
                        border: '1px solid',
                        borderColor: getInvestmentBand(calculateGrandTotal()) === 'Small' ? 'success.700' : 
                                     getInvestmentBand(calculateGrandTotal()) === 'Medium' ? 'warning.700' : 'error.700'
                    }}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingUpIcon color={getInvestmentBand(calculateGrandTotal()) === 'Small' ? 'success' : 
                                                       getInvestmentBand(calculateGrandTotal()) === 'Medium' ? 'warning' : 'error'} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Investment Band</Typography>
                                    <Typography variant="h5" fontWeight={700} color={
                                        getInvestmentBand(calculateGrandTotal()) === 'Small' ? 'success.main' : 
                                        getInvestmentBand(calculateGrandTotal()) === 'Medium' ? 'warning.main' : 'error.main'
                                    }>
                                        {getInvestmentBand(calculateGrandTotal())}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Two Column Layout */}
            <Grid container spacing={3}>
                {/* LEFT COLUMN - Team Member Search */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, height: '100%', position: 'sticky', top: 16, bgcolor: 'transparent' }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SearchIcon fontSize="small" color="primary" />
                            Find Team Members
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Search past allocations for staff, associates & partners
                        </Typography>
                        
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by name..."
                            value={staffSearchQuery}
                            onChange={(e) => setStaffSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                                endAdornment: isSearchingStaff ? <CircularProgress size={18} /> : null
                            }}
                            sx={{ mb: 2 }}
                        />
                        
                        {/* Pinned Staff - Show at top when we have pins */}
                        {pinnedStaff.length > 0 && (
                            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.900', borderRadius: 1, border: '1px solid', borderColor: 'warning.700' }}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                    <PushPinIcon sx={{ fontSize: 14 }} />
                                    Pinned ({pinnedStaff.length})
                                </Typography>
                                {pinnedStaff.map((staff) => (
                                    <Box key={staff.person_name} sx={{ mb: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" fontWeight={500}>{staff.person_name}</Typography>
                                            <IconButton size="small" onClick={() => handleUnpinStaff(staff.person_name)}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        {/* Show allocations inline for pinned staff */}
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                            {staff.allocations.slice(0, 3).map((alloc, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={`${alloc.role || 'Role'}: ${alloc.currency === 'USD' ? '$' : alloc.currency === 'EUR' ? '€' : '£'}${alloc.fee_per_day || alloc.rca_rate || 0}`}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleAddStaffToTeam(staff, alloc)}
                                                    onDelete={() => handleAddStaffToTeam(staff, alloc)}
                                                    deleteIcon={<PersonAddIcon fontSize="small" />}
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        
                        {/* Search Results */}
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {staffSearchResults.map((staff) => (
                                <Box 
                                    key={staff.person_name} 
                                    sx={{ 
                                        p: 1.5, 
                                        mb: 1, 
                                        bgcolor: pinnedStaff.find(p => p.person_name === staff.person_name) ? 'warning.900' : 'action.hover',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>{staff.person_name}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Chip 
                                                    label={staff.person_type.replace('_', ' ')} 
                                                    size="small" 
                                                    color={staff.person_type === 'iod_staff' ? 'primary' : 'default'}
                                                    sx={{ textTransform: 'capitalize', fontSize: '0.65rem', height: 20 }}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {staff.project_count} projects
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Tooltip title={pinnedStaff.find(p => p.person_name === staff.person_name) ? 'Unpin' : 'Pin'}>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => pinnedStaff.find(p => p.person_name === staff.person_name) 
                                                    ? handleUnpinStaff(staff.person_name) 
                                                    : handlePinStaff(staff)
                                                }
                                                color={pinnedStaff.find(p => p.person_name === staff.person_name) ? 'warning' : 'default'}
                                            >
                                                {pinnedStaff.find(p => p.person_name === staff.person_name) 
                                                    ? <PushPinIcon fontSize="small" /> 
                                                    : <PushPinOutlinedIcon fontSize="small" />
                                                }
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    
                                    {/* Allocations - always visible, with project details */}
                                    <Stack spacing={0.5}>
                                        {staff.allocations.map((alloc, idx) => (
                                            <Box 
                                                key={idx} 
                                                sx={{ 
                                                    p: 0.75,
                                                    bgcolor: 'action.hover',
                                                    borderRadius: 0.5,
                                                    cursor: 'pointer',
                                                    '&:hover': { bgcolor: 'primary.900' }
                                                }}
                                                onClick={() => handleAddStaffToTeam(staff, alloc)}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="caption" fontWeight={500}>{alloc.role || 'No role'}</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {alloc.fee_per_day && (
                                                            <Typography variant="caption" fontWeight={600}>
                                                                {alloc.currency === 'USD' ? '$' : alloc.currency === 'EUR' ? '€' : '£'}{alloc.fee_per_day}
                                                            </Typography>
                                                        )}
                                                        {alloc.rca_rate && !alloc.fee_per_day && (
                                                            <Typography variant="caption" fontWeight={600} color="secondary.main">
                                                                RCA: {alloc.currency === 'USD' ? '$' : alloc.currency === 'EUR' ? '€' : '£'}{alloc.rca_rate}
                                                            </Typography>
                                                        )}
                                                        <PersonAddIcon fontSize="small" color="primary" sx={{ fontSize: 16 }} />
                                                    </Box>
                                                </Box>
                                                <Tooltip title={`${alloc.project_name || 'No project name'} - ${alloc.client_name || 'No client'}`} placement="top">
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                        {alloc.project_number} • {alloc.project_name || 'No project name'}
                                                    </Typography>
                                                </Tooltip>
                                                <Tooltip title={alloc.client_name || 'No client'} placement="bottom">
                                                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                                        {alloc.client_name || 'No client'}
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                            {staffSearchQuery.length >= 2 && staffSearchResults.length === 0 && !isSearchingStaff && (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    No results found
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* RIGHT COLUMN - Cost Tables */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'transparent' }}>
                        {/* Team Cost Estimate Table */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}>Team Cost Estimate</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Copy as markdown table">
                                    <IconButton size="small" onClick={handleCopyAsTable} color={copied ? 'success' : 'default'}>
                                        <TableChartIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Copy summary">
                                    <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Clear team">
                                    <IconButton size="small" onClick={handleClearTeam} color="error">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Button size="small" startIcon={<AddIcon />} onClick={handleAddConsultant} variant="outlined">
                                    Add Row
                                </Button>
                            </Box>
                        </Box>
                        <TableContainer sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell sx={{ width: 220 }}><strong>Name</strong></TableCell>
                                        <TableCell sx={{ width: 200 }}><strong>Role</strong></TableCell>
                                        <TableCell align="center" sx={{ width: 140 }}><strong>Day Rate</strong></TableCell>
                                        <TableCell align="center" sx={{ width: 160 }}><strong>Days</strong></TableCell>
                                        <TableCell align="right" sx={{ width: 120 }}><strong>Cost</strong></TableCell>
                                        <TableCell align="center" sx={{ width: 50 }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {consultants.map(consultant => (
                                        <TableRow key={consultant.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    placeholder="Name"
                                                    value={consultant.name}
                                                    onChange={(e) => handleUpdateConsultant(consultant.id, 'name', e.target.value)}
                                                    fullWidth
                                                    variant="standard"
                                                    InputProps={{ disableUnderline: !consultant.name }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    placeholder="Role"
                                                    value={consultant.role}
                                                    onChange={(e) => handleUpdateConsultant(consultant.id, 'role', e.target.value)}
                                                    fullWidth
                                                    variant="standard"
                                                    InputProps={{ disableUnderline: !consultant.role }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title={consultant.sourceRate ? `Source: ${consultant.sourceCurrency || 'GBP'} ${consultant.sourceRate}` : ''}>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={consultant.rate}
                                                        onChange={(e) => handleUpdateConsultant(consultant.id, 'rate', e.target.value)}
                                                        sx={{ width: 120 }}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment>
                                                        }}
                                                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <IconButton size="small" onClick={() => handleUpdateConsultant(consultant.id, 'days', consultant.days - 1)}>
                                                        <RemoveIcon fontSize="small" />
                                                    </IconButton>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={consultant.days}
                                                        onChange={(e) => handleUpdateConsultant(consultant.id, 'days', e.target.value)}
                                                        sx={{ width: 80 }}
                                                        inputProps={{ min: 0, style: { textAlign: 'center' } }}
                                                    />
                                                    <IconButton size="small" onClick={() => handleUpdateConsultant(consultant.id, 'days', consultant.days + 1)}>
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight={consultant.days > 0 ? 600 : 400} color={consultant.days > 0 ? 'text.primary' : 'text.secondary'}>
                                                    {getCurrencySymbol()}{(consultant.days * consultant.rate).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleRemoveConsultant(consultant.id)}
                                                    disabled={consultants.length <= 1}
                                                    sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ bgcolor: 'primary.900' }}>
                                        <TableCell colSpan={3}><strong>Team Subtotal</strong></TableCell>
                                        <TableCell align="center"><strong>{calculateTotalDays()} days</strong></TableCell>
                                        <TableCell align="right"><strong>{getCurrencySymbol()}{calculateConsultantCost().toLocaleString()}</strong></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Additional Costs Table */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>Additional Costs</Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={handleAddLineItem}>
                                Add Item
                            </Button>
                        </Box>
                        <TableContainer sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableBody>
                                    {costLineItems.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    value={item.name}
                                                    onChange={(e) => handleUpdateLineItem(index, 'name', e.target.value)}
                                                    fullWidth
                                                    variant="standard"
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{ width: 150 }}>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    value={item.amount}
                                                    onChange={(e) => handleUpdateLineItem(index, 'amount', e.target.value)}
                                                    sx={{ width: 120 }}
                                                    inputProps={{ min: 0, style: { textAlign: 'right' } }}
                                                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>{getCurrencySymbol()}</Typography> }}
                                                />
                                            </TableCell>
                                            <TableCell align="center" sx={{ width: 40 }}>
                                                <IconButton size="small" onClick={() => handleRemoveLineItem(index)} sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell><strong>Additional Subtotal</strong></TableCell>
                                        <TableCell align="right"><strong>{getCurrencySymbol()}{calculateLineItemsTotal().toLocaleString()}</strong></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Grand Total */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'primary.dark', borderRadius: 2 }}>
                            <Typography variant="h6" color="white" fontWeight={600}>Total Estimate</Typography>
                            <Typography variant="h4" color="white" fontWeight={700}>
                                {getCurrencySymbol()}{calculateGrandTotal().toLocaleString()}
                            </Typography>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Indicative estimate for planning purposes only.
                            </Typography>
                            <Button size="small" variant="outlined" onClick={handleReset}>
                                Reset All
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default CostEstimatorPage;
